// HeatmapLegend.js — the colour bands used by the grid, rarest first, plus
// "Never (yet)" for empty cells. Vertical list on desktop, one compact row
// on mobile. The bands come from legendBands() in utils, so the legend always
// matches the cell colours exactly.

import React from "react";
import { Box, Typography } from "@mui/material";
import { BRAND } from "../../theme";
import { EMPTY_CELL, rgb } from "./utils";

export const EYEBROW_SX = {
  color: BRAND.textSecondary,
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  lineHeight: 1,
};

function Swatch({ color, width, height }) {
  return (
    <Box
      sx={{
        width,
        height,
        flexShrink: 0,
        borderRadius: "5px",
        background: color ? rgb(color) : EMPTY_CELL,
        border: color ? "none" : "1px solid rgba(255,255,255,0.14)",
        boxShadow: color ? "inset 0 -2px 0 rgba(0,0,0,0.18)" : "none",
      }}
    />
  );
}

export default function HeatmapLegend({ bands, horizontal = false }) {
  const items = [{ label: "Never (yet)", color: null }, ...bands.map((b) => ({ label: b.label, color: b.color }))];

  if (horizontal) {
    return (
      <Box>
        <Typography sx={{ ...EYEBROW_SX, mb: 1.25 }}>Times it's happened</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 0.75 }}>
          {items.map((it) => (
            <Box key={it.label} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6, minWidth: 0 }}>
              <Swatch color={it.color} width="100%" height={14} />
              <Typography
                sx={{
                  color: "#d7dae3",
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFeatureSettings: '"tnum"',
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                  textAlign: "center",
                }}
              >
                {it.label === "Never (yet)" ? "Never" : it.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ ...EYEBROW_SX, mb: 1.5 }}>Times it's happened</Typography>
      {/* Two columns, filled top-to-bottom, so rarest-to-commonest still reads in order. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: `repeat(${Math.ceil(items.length / 2)}, auto)`,
          gridAutoFlow: "column",
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {items.map((it) => (
          <Box key={it.label} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Swatch color={it.color} width={22} height={22} />
            <Typography sx={{ color: "#e6e8ee", fontSize: 13.5, fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
              {it.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
